/**
 * Admin Status Management Helpers
 * Handles active/inactive status changes and validations
 */

/**
 * Count active admins in configuration
 * @param {Array<Object>} configs - Configuration array
 * @returns {number} Number of active admins
 */
export const countActiveAdmins = (configs) => configs.filter((c) => c.isActive).length;

/**
 * Get active admins from configuration
 * @param {Array<Object>} configs - Configuration array
 * @returns {Array<Object>} Active admin configurations
 */
export const getActiveAdmins = (configs) => configs.filter((c) => c.isActive);

/**
 * Validate if changing active status is allowed
 * @param {Array<Object>} configs - Current configurations
 * @param {boolean} newValue - New active status value
 * @param {Function} messageApi - Message notification API
 * @param {Object} constants - Message constants
 * @returns {boolean} Whether the change is allowed
 */
export const validateActiveStatusChange = (configs, newValue, messageApi, constants) => {
  if (newValue === false) {
    const activeCount = countActiveAdmins(configs);
    if (activeCount === 0) {
      messageApi?.warning?.(
        constants?.NO_ACTIVE_ADMINS || "At least one admin must remain active",
        constants?.WARNING || 3
      );
      return false;
    }
  }
  return true;
};

/**
 * Get the only active admin if exists
 * @param {Array<Object>} configs - Configuration array
 * @returns {Object|null} Active admin config or null
 */
export const getOnlyActiveAdmin = (configs) => {
  const activeAdmins = getActiveAdmins(configs);
  return activeAdmins.length === 1 ? activeAdmins[0] : null;
};

/**
 * Check if admin is the only active one
 * @param {Array<Object>} configs - Configuration array
 * @param {number} configId - Config id to check
 * @returns {boolean} Whether this is the only active admin
 */
export const isOnlyActiveAdmin = (configs, configId) => {
  const activeAdmins = getActiveAdmins(configs);
  return activeAdmins.length === 1 && activeAdmins[0].id === configId;
};
